import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Config from '../../lib/Config'
import { Language } from '../../lib/Enums/Language'
import Headline from '../Elements/Headline'
import { SheetType } from './lib/Enums/SheetType'
import { useAccountings } from './lib/Hooks/Accounting'
import { useBudgets } from './lib/Hooks/Budget'

import { MdArrowDropDown, MdHelpOutline } from 'react-icons/md'
import { useOnClickOutside } from 'usehooks-ts'
import { useSpreadsheets } from './lib/Hooks/Spreadsheet'
interface Props {
  type: SheetType
  title: string
  url: string
}

// The article this links to is translated on WWW under the same
// translationKey ("hvad-er-et-regneark"), but each locale has its own domain
// and slug — see WelcomeSpreadsheet.tsx for the same pattern.
const SPREADSHEET_ARTICLE_SLUG: Record<Language, string> = {
  [Language.DANISH]: 'hvad-er-et-regneark',
  [Language.GERMAN]: 'tabelle',
  [Language.ENGLISH]: 'spreadsheet',
}

type titleType = {
  title: string
  url: string
  isFavorite: boolean
}

type categoryKey = 'favorites' | 'budgets' | 'accountings' | 'spreadsheets'

type categorizedTitles = Record<categoryKey, titleType[]>

const EMPTY_CATEGORIES: categorizedTitles = {
  favorites: [],
  budgets: [],
  accountings: [],
  spreadsheets: [],
}

// Strips a leading emoji (and the whitespace after it) so titles like
// "💰 Lønkonto" sort under "L", not under the emoji.
const stripLeadingEmoji = (str: string) =>
  str.replace(/^[\p{Extended_Pictographic}\uFE0F\u200D]+\s*/u, '')

const sortByTitle = (a: titleType, b: titleType) =>
  stripLeadingEmoji(a.title).localeCompare(stripLeadingEmoji(b.title), 'da', {
    sensitivity: 'base',
  })

const CATEGORIES: Array<{ key: categoryKey; labelKey: string }> = [
  { key: 'favorites', labelKey: 'configuration:sheetTitle.categoryFavorites' },
  { key: 'budgets', labelKey: 'configuration:sheetTitle.categoryBudgets' },
  {
    key: 'accountings',
    labelKey: 'configuration:sheetTitle.categoryAccountings',
  },
  {
    key: 'spreadsheets',
    labelKey: 'configuration:sheetTitle.categorySpreadsheets',
  },
]

const SheetTitle: FC<Props> = ({ type, title, url }) => {
  const { t, i18n } = useTranslation('configuration')
  const { data: budgets } = useBudgets()
  const { data: accountings } = useAccountings()
  const { data: spreadsheets } = useSpreadsheets()

  const [titles, setTitles] = useState<categorizedTitles>(EMPTY_CATEGORIES)
  const [active, setActive] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useOnClickOutside(ref, () => setActive(false))

  useEffect(() => {
    if (budgets && accountings && spreadsheets) {
      const budgetTitles: titleType[] = budgets
        .filter((budget) => !budget.is_archived)
        .map((budget) => ({
          title: budget.name
            ? budget.name
            : t('configuration:sheetTitle.untitledBudget', { id: budget.id }),
          url: `/budget/${budget.id}`,
          isFavorite: !!budget.is_favorite,
        }))
      const accountingTitles: titleType[] = accountings
        .filter((accounting) => !accounting.is_archived)
        .map((accounting) => ({
          title: accounting.name
            ? accounting.name
            : t('configuration:sheetTitle.untitledAccounting', {
              id: accounting.id,
            }),
          url: `/accounting/${accounting.id}`,
          isFavorite: !!accounting.is_favorite,
        }))
      const spreadsheetTitles: titleType[] = spreadsheets
        .filter((spreadsheet) => !spreadsheet.is_archived)
        .map((spreadsheet) => ({
          title: spreadsheet.name
            ? spreadsheet.name
            : t('configuration:sheetTitle.untitledSpreadsheet', {
              id: spreadsheet.id,
            }),
          url: `/spreadsheet/${spreadsheet.id}`,
          isFavorite: !!spreadsheet.is_favorite,
        }))

      const favorites = [
        ...budgetTitles,
        ...accountingTitles,
        ...spreadsheetTitles,
      ].filter((item) => item.isFavorite)

      const notCurrent = (item: titleType) => item.url !== url

      const categorized: categorizedTitles = {
        favorites: favorites.filter(notCurrent).sort(sortByTitle),
        budgets: budgetTitles
          .filter((item) => !item.isFavorite)
          .filter(notCurrent)
          .sort(sortByTitle),
        accountings: accountingTitles
          .filter((item) => !item.isFavorite)
          .filter(notCurrent)
          .sort(sortByTitle),
        spreadsheets: spreadsheetTitles
          .filter((item) => !item.isFavorite)
          .filter(notCurrent)
          .sort(sortByTitle),
      }

      setTitles(categorized)
    }
  }, [budgets, accountings, spreadsheets, url])

  const titleCount =
    titles.favorites.length +
    titles.budgets.length +
    titles.accountings.length +
    titles.spreadsheets.length

  const getTypeTitle = () => {
    if (type === SheetType.BUDGET) return t('configuration:sheetTitle.budget')
    if (type === SheetType.ACCOUNTING) { return t('configuration:sheetTitle.accounting') }
    if (type === SheetType.SPREADSHEET) { return t('configuration:sheetTitle.spreadsheet') }
    return t('configuration:sheetTitle.unknown')
  }

  const hasTypeAnnotation = getTypeTitle() !== title

  const helpLink = type === SheetType.SPREADSHEET && (
    <a
      href={`${Config.WWW_DOMAIN[i18n.language as Language] ?? Config.WWW_DOMAIN[Language.DANISH]}/regneark/${SPREADSHEET_ARTICLE_SLUG[i18n.language as Language] ?? SPREADSHEET_ARTICLE_SLUG[Language.DANISH]}`}
      target='_blank'
      rel='noreferrer'
      title={t('configuration:sheetTitle.helpArticle')}
      aria-label={t('configuration:sheetTitle.helpArticle')}
      className='ml-2 text-black'
    >
      <MdHelpOutline size='1.25rem' />
    </a>
  )

  return (
    <div className='relative' ref={ref}>
      {hasTypeAnnotation && (
        <div className='flex items-center'>
          <h2>{getTypeTitle()}</h2>
          {helpLink}
        </div>
      )}
      <div className='flex items-center'>
        <div
          onClick={() => setActive(!active)}
          className={`${titleCount > 0 ? 'cursor-pointer' : ''} inline-flex`}
        >
          <Headline>{title}</Headline>
          {titleCount > 0 && <MdArrowDropDown size='1.5rem' />}
        </div>
        {!hasTypeAnnotation && helpLink}
      </div>
      {active && (
        <div className='absolute bg-white py-2 z-100 border border-gray-200'>
          {CATEGORIES.map(({ key, labelKey }) => {
            const items = titles[key]
            if (items.length === 0) return null

            return (
              <div key={key}>
                <div className='px-2 pt-4 pb-2 text-xs font-semibold text-gray-400 first:pt-2'>
                  {t(labelKey)}
                </div>
                {items.map((item) => (
                  <a
                    key={item.url}
                    href={item.url}
                    className='px-2 py-1.5 hover:bg-gray-50 flex'
                  >
                    {item.title}
                  </a>
                ))}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default SheetTitle
